// Typed mirror of the design tokens declared in `src/index.css`.
//
// The stylesheet stays the single runtime source of truth; this module exists
// so TypeScript code and tests can reason about the same values. `tokens.test.ts`
// parses the stylesheet and fails when the two drift apart, and proves the
// WCAG 2.2 AA contrast of every pair listed in CONTRAST_REQUIREMENTS.

import { contrastRatio, parseHslTriple } from "./contrast";

export type ThemeName = "light" | "dark";

/**
 * The five semantic tones every status in the product resolves to. Screens pick
 * a tone; they never pick a colour.
 */
export type StatusTone = "neutral" | "info" | "success" | "warning" | "danger";

export const STATUS_TONES: readonly StatusTone[] = [
  "neutral",
  "info",
  "success",
  "warning",
  "danger",
];

/** Matches `theme.screens` in `tailwind.config.js`, in pixels. */
export const BREAKPOINTS = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

export type DeviceTier = "mobile" | "tablet" | "desktop";

/**
 * The three shells. Mobile stacks and hides navigation behind a drawer, tablet
 * keeps an icon rail, desktop shows the full sidebar.
 */
export function deviceTierFor(width: number): DeviceTier {
  if (width < BREAKPOINTS.md) return "mobile";
  if (width < BREAKPOINTS.lg) return "tablet";
  return "desktop";
}

/**
 * WCAG 2.2 success criterion 2.5.8 sets the minimum target at 24x24 CSS px.
 * Primary touch targets use the comfortable size instead.
 */
export const TARGET_SIZE_PX = {
  minimum: 24,
  comfortable: 44,
} as const;

/** Milliseconds behind `--motion-*`. Reduced motion collapses these to 1ms. */
export const MOTION_MS = {
  fast: 120,
  base: 180,
  slow: 260,
} as const;

export const ELEVATION_LEVELS = [0, 1, 2, 3] as const;

/**
 * Colour tokens as the bare `H S% L%` triples the stylesheet declares. `dark`
 * is the fully resolved theme: keys the stylesheet does not restate under
 * `.dark` inherit the light value.
 */
export const colorTokens: Record<ThemeName, Record<string, string>> = {
  light: {
    canvas: "220 20% 98%",
    background: "0 0% 100%",
    foreground: "222 24% 11%",
    card: "0 0% 100%",
    "card-foreground": "222 24% 11%",
    popover: "0 0% 100%",
    "popover-foreground": "222 24% 11%",
    primary: "222 24% 13%",
    "primary-foreground": "0 0% 100%",
    secondary: "220 16% 96%",
    "secondary-foreground": "222 24% 15%",
    muted: "220 16% 96%",
    "muted-foreground": "220 10% 38%",
    accent: "220 16% 94%",
    "accent-foreground": "222 24% 15%",
    destructive: "0 72% 45%",
    "destructive-foreground": "0 0% 100%",
    link: "221 83% 40%",
    border: "220 14% 89%",
    "border-strong": "220 11% 54%",
    input: "220 10% 44%",
    ring: "221 83% 45%",
    "tone-neutral-surface": "220 16% 96%",
    "tone-neutral-foreground": "220 12% 30%",
    "tone-neutral-border": "220 13% 86%",
    "tone-neutral-solid": "220 9% 42%",
    "tone-info-surface": "214 100% 97%",
    "tone-info-foreground": "221 70% 32%",
    "tone-info-border": "214 70% 88%",
    "tone-info-solid": "221 83% 45%",
    "tone-success-surface": "152 55% 95%",
    "tone-success-foreground": "158 80% 21%",
    "tone-success-border": "152 40% 82%",
    "tone-success-solid": "158 75% 27%",
    "tone-warning-surface": "45 100% 94%",
    "tone-warning-foreground": "28 88% 26%",
    "tone-warning-border": "45 80% 80%",
    "tone-warning-solid": "32 90% 36%",
    "tone-danger-surface": "0 86% 97%",
    "tone-danger-foreground": "0 70% 36%",
    "tone-danger-border": "0 70% 88%",
    "tone-danger-solid": "0 72% 45%",
    "sidebar-background": "220 20% 98%",
    "sidebar-foreground": "222 15% 28%",
    "sidebar-primary": "222 24% 13%",
    "sidebar-primary-foreground": "0 0% 100%",
    "sidebar-accent": "220 16% 94%",
    "sidebar-accent-foreground": "222 24% 15%",
    "sidebar-border": "220 14% 89%",
    "sidebar-ring": "221 83% 45%",
  },
  dark: {
    canvas: "224 26% 6%",
    background: "224 22% 9%",
    foreground: "220 20% 96%",
    card: "224 20% 12%",
    "card-foreground": "220 20% 96%",
    popover: "224 20% 12%",
    "popover-foreground": "220 20% 96%",
    primary: "220 20% 96%",
    "primary-foreground": "224 24% 10%",
    secondary: "223 17% 17%",
    "secondary-foreground": "220 20% 96%",
    muted: "223 17% 17%",
    "muted-foreground": "220 12% 70%",
    accent: "223 17% 20%",
    "accent-foreground": "220 20% 96%",
    destructive: "0 72% 48%",
    "destructive-foreground": "0 0% 100%",
    link: "213 94% 72%",
    border: "223 15% 21%",
    "border-strong": "223 12% 42%",
    input: "223 13% 46%",
    ring: "217 91% 62%",
    "tone-neutral-surface": "223 17% 17%",
    "tone-neutral-foreground": "220 13% 72%",
    "tone-neutral-border": "223 14% 26%",
    "tone-neutral-solid": "220 10% 62%",
    "tone-info-surface": "221 60% 15%",
    "tone-info-foreground": "213 92% 76%",
    "tone-info-border": "221 50% 26%",
    "tone-info-solid": "213 90% 64%",
    "tone-success-surface": "158 55% 12%",
    "tone-success-foreground": "150 70% 64%",
    "tone-success-border": "158 45% 22%",
    "tone-success-solid": "152 65% 48%",
    "tone-warning-surface": "36 70% 13%",
    "tone-warning-foreground": "43 95% 62%",
    "tone-warning-border": "36 60% 24%",
    "tone-warning-solid": "40 92% 52%",
    "tone-danger-surface": "0 55% 15%",
    "tone-danger-foreground": "0 90% 78%",
    "tone-danger-border": "0 45% 27%",
    "tone-danger-solid": "0 75% 58%",
    "sidebar-background": "224 24% 8%",
    "sidebar-foreground": "220 14% 72%",
    "sidebar-primary": "220 20% 96%",
    "sidebar-primary-foreground": "224 24% 10%",
    "sidebar-accent": "223 17% 17%",
    "sidebar-accent-foreground": "220 20% 96%",
    "sidebar-border": "223 15% 21%",
    "sidebar-ring": "217 91% 62%",
  },
};

export const CONTRAST_MINIMUM = {
  /** WCAG 2.2 1.4.3 body text. */
  text: 4.5,
  /** WCAG 2.2 1.4.11 non-text contrast for control boundaries and indicators. */
  nonText: 3,
} as const;

export type ContrastRequirement = {
  foreground: string;
  background: string;
  minimum: number;
  usage: string;
};

const TEXT_PAIRS: ReadonlyArray<[string, string, string]> = [
  ["foreground", "background", "body text on a surface"],
  ["foreground", "canvas", "body text on the page canvas"],
  ["foreground", "card", "body text inside a card"],
  ["muted-foreground", "background", "secondary text"],
  ["muted-foreground", "canvas", "secondary text on the canvas"],
  ["muted-foreground", "muted", "secondary text on a muted fill"],
  ["foreground", "muted", "read-only field value on a muted fill"],
  ["primary-foreground", "primary", "primary button label"],
  ["secondary-foreground", "secondary", "secondary button label"],
  ["accent-foreground", "accent", "hovered menu item label"],
  ["destructive-foreground", "destructive", "destructive button label"],
  ["link", "background", "inline link"],
  ["sidebar-foreground", "sidebar-background", "navigation label"],
  ["sidebar-primary-foreground", "sidebar-primary", "active navigation label"],
  ["sidebar-accent-foreground", "sidebar-accent", "hovered navigation label"],
];

const NON_TEXT_PAIRS: ReadonlyArray<[string, string, string]> = [
  ["border-strong", "background", "control boundary"],
  ["input", "background", "text input boundary"],
  ["ring", "background", "focus indicator"],
  ["ring", "canvas", "focus indicator on the canvas"],
  ["sidebar-ring", "sidebar-background", "navigation focus indicator"],
];

/**
 * Every pair the product depends on to convey meaning. Decorative tokens such
 * as `--border` are deliberately absent: they separate content but never carry
 * information on their own.
 */
export const CONTRAST_REQUIREMENTS: readonly ContrastRequirement[] = [
  ...TEXT_PAIRS.map(([foreground, background, usage]) => ({
    foreground,
    background,
    usage,
    minimum: CONTRAST_MINIMUM.text,
  })),
  ...NON_TEXT_PAIRS.map(([foreground, background, usage]) => ({
    foreground,
    background,
    usage,
    minimum: CONTRAST_MINIMUM.nonText,
  })),
  ...STATUS_TONES.flatMap((tone) => [
    {
      foreground: `tone-${tone}-foreground`,
      background: `tone-${tone}-surface`,
      usage: `${tone} status label on its own fill`,
      minimum: CONTRAST_MINIMUM.text,
    },
    {
      foreground: `tone-${tone}-foreground`,
      background: "background",
      usage: `${tone} status label on a plain surface`,
      minimum: CONTRAST_MINIMUM.text,
    },
    {
      foreground: `tone-${tone}-foreground`,
      background: "muted",
      usage: `${tone} status label in a read-only field`,
      minimum: CONTRAST_MINIMUM.text,
    },
    {
      foreground: `tone-${tone}-solid`,
      background: "background",
      usage: `${tone} status marker`,
      minimum: CONTRAST_MINIMUM.nonText,
    },
  ]),
];

/** Contrast ratio between two colour tokens in a given theme. */
export function tokenContrast(
  theme: ThemeName,
  foregroundToken: string,
  backgroundToken: string,
): number {
  const foreground = parseHslTriple(colorTokens[theme][foregroundToken]);
  const background = parseHslTriple(colorTokens[theme][backgroundToken]);

  if (!foreground || !background) {
    throw new Error(
      `Unknown colour token in ${theme}: ${foregroundToken} on ${backgroundToken}`,
    );
  }

  return contrastRatio(foreground, background);
}
